import React, { useEffect, useMemo, useState } from "react";
import { api, authedUrl, fmtMoney, fmtNum } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Download, FileText, FileSpreadsheet, CheckSquare, Square } from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0, 10);
const firstOfMonthISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

export default function TradingHistoryReport() {
  const [clients, setClients] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [start, setStart] = useState(firstOfMonthISO());
  const [end, setEnd] = useState(todayISO());
  const [q, setQ] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/clients");
      setClients(data);
    })();
  }, []);

  const filtered = useMemo(() => clients.filter(c =>
    !q || c.name.toLowerCase().includes(q.toLowerCase()) || (c.pan || "").toLowerCase().includes(q.toLowerCase())
  ), [clients, q]);

  const toggle = (id) => {
    const s = new Set(selectedIds);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelectedIds(s);
  };
  const selectAll = () => setSelectedIds(new Set(filtered.map(c => c.id)));
  const clearAll = () => setSelectedIds(new Set());

  const buildQS = () => {
    const ids = Array.from(selectedIds).join(",");
    return `client_ids=${encodeURIComponent(ids)}&start=${start}&end=${end}`;
  };

  const runReport = async () => {
    if (selectedIds.size === 0) return toast.error("Select at least one client");
    if (end < start) return toast.error("End date must be on or after start date");
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/trading-history?${buildQS()}`);
      setReport(data);
      toast.success(`Loaded report for ${data.clients.length} client(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  const canDownload = selectedIds.size > 0 && start && end;

  return (
    <div className="space-y-6">
      <header>
        <div className="label-caps">Report</div>
        <h1 className="font-heading font-extrabold text-3xl sm:text-4xl mt-2 tracking-tight">Trading History Report</h1>
        <p className="text-slate-400 text-sm mt-2">View & download consolidated trading history and realized P&L for one or more clients over a chosen period.</p>
      </header>

      <section className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Filters */}
        <div className="card-tactical p-6 lg:col-span-5">
          <div className="label-caps mb-1">Step 1</div>
          <h2 className="font-heading font-bold text-lg mb-4">Select Clients & Period</h2>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <Field label="Start Date">
              <Input type="date" value={start} onChange={e => setStart(e.target.value)} data-testid="input-report-start-date" className="bg-slate-900/70 border-slate-800 h-11" />
            </Field>
            <Field label="End Date">
              <Input type="date" value={end} onChange={e => setEnd(e.target.value)} data-testid="input-report-end-date" className="bg-slate-900/70 border-slate-800 h-11" />
            </Field>
          </div>

          <div className="flex items-center justify-between mb-2">
            <div className="label-caps">Clients ({selectedIds.size} selected)</div>
            <div className="flex gap-2 text-xs">
              <button type="button" onClick={selectAll} data-testid="btn-select-all-clients" className="text-emerald-400 hover:underline">All</button>
              <button type="button" onClick={clearAll} data-testid="btn-clear-clients" className="text-slate-400 hover:underline">Clear</button>
            </div>
          </div>
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search..." data-testid="input-report-client-search" className="bg-slate-900/70 border-slate-800 mb-2" />
          <div className="max-h-72 overflow-y-auto border border-slate-800 rounded-lg divide-y divide-slate-800/60" data-testid="list-report-clients">
            {filtered.length === 0 && <div className="p-4 text-sm text-slate-500">No clients.</div>}
            {filtered.map(c => {
              const sel = selectedIds.has(c.id);
              return (
                <label key={c.id} className={`flex items-center gap-3 p-3 cursor-pointer text-sm ${sel ? "bg-emerald-500/5" : "hover:bg-slate-800/40"}`} data-testid={`report-client-row-${c.id}`}>
                  <Checkbox checked={sel} onCheckedChange={() => toggle(c.id)} data-testid={`report-client-check-${c.id}`} />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate">{c.name}</div>
                    {(c.pan || (c.tags || []).length > 0) && (
                      <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">
                        {c.pan && <span className="font-mono-tabular">{c.pan}</span>}
                        {(c.tags || []).map(t => <span key={t} className="px-1.5 py-0.5 rounded-full text-[10px] border bg-slate-800/50 border-slate-700 text-slate-300">{t}</span>)}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            <Button onClick={runReport} disabled={!canDownload || loading} data-testid="btn-view-report" className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-semibold flex-1 min-w-[120px]">
              {loading ? "Loading..." : "View Report"}
            </Button>
            <a href={canDownload ? authedUrl(`/reports/trading-history.csv?${buildQS()}`) : undefined} data-testid="btn-download-report-csv">
              <Button variant="outline" disabled={!canDownload} className="border-slate-700 text-slate-800 hover:text-emerald-400">
                <FileSpreadsheet size={14} className="mr-2" /> CSV
              </Button>
            </a>
            <a href={canDownload ? authedUrl(`/reports/trading-history.pdf?${buildQS()}`) : undefined} target="_blank" rel="noopener" data-testid="btn-download-report-pdf">
              <Button variant="outline" disabled={!canDownload} className="border-slate-700 text-slate-800 hover:text-emerald-400">
                <FileText size={14} className="mr-2" /> PDF
              </Button>
            </a>
          </div>
        </div>

        {/* Preview */}
        <div className="lg:col-span-7 space-y-6">
          {!report ? (
            <div className="card-tactical p-10 text-center text-slate-500 text-sm">
              <div className="label-caps mb-2">Step 2</div>
              Pick your clients and dates, then click <span className="text-emerald-400 font-semibold">View Report</span> to preview or download.
            </div>
          ) : (
            <>
              <div className="card-tactical p-6" data-testid="report-grand-totals">
                <div className="label-caps">Consolidated</div>
                <h2 className="font-heading font-bold text-xl mt-1 mb-4">
                  {report.start} → {report.end}
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  <Stat label="Trades" value={fmtNum(report.grand_totals.trade_count)} />
                  <Stat label="Buy Gross" value={fmtMoney(report.grand_totals.buy_gross)} />
                  <Stat label="Sell Gross" value={fmtMoney(report.grand_totals.sell_gross)} />
                  <Stat label="Commission" value={fmtMoney(report.grand_totals.commission)} />
                  <Stat label="Realized P&L" value={fmtMoney(report.grand_totals.realized_pnl)} tone={report.grand_totals.realized_pnl >= 0 ? "up" : "down"} />
                </div>
              </div>

              {report.clients.map(c => (
                <div key={c.client_id} className="card-tactical p-6" data-testid={`report-client-block-${c.client_id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div>
                      <h3 className="font-heading font-bold text-lg">{c.client.name}</h3>
                      {c.client.pan && <div className="text-xs text-slate-400 font-mono-tabular mt-0.5">{c.client.pan}</div>}
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs">
                      <Chip label="Trades" value={fmtNum(c.totals.trade_count)} />
                      <Chip label="Realized" value={fmtMoney(c.totals.realized_pnl)} tone={c.totals.realized_pnl >= 0 ? "up" : "down"} />
                      <Chip label="Commission" value={fmtMoney(c.totals.commission)} />
                    </div>
                  </div>
                  {c.trades.length === 0 ? (
                    <div className="py-6 text-center text-slate-500 text-sm border border-dashed border-slate-800 rounded-lg">No trades in this period.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="tactical" data-testid={`report-client-trades-${c.client_id}`}>
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Account</th>
                            <th>Type</th>
                            <th>Symbol</th>
                            <th className="!text-right">Qty</th>
                            <th className="!text-right">Price</th>
                            <th className="!text-right">Comm.</th>
                            <th className="!text-right">Realized P&L</th>
                            <th>Entered By</th>
                          </tr>
                        </thead>
                        <tbody>
                          {c.trades.map(t => (
                            <tr key={t.id}>
                              <td className="text-slate-300">{t.trade_date}</td>
                              <td className="text-slate-300 text-xs">{t.account_name || "—"}</td>
                              <td><span className={`px-2 py-0.5 rounded text-[10px] font-bold ${t.trade_type === "BUY" ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400"}`}>{t.trade_type}</span></td>
                              <td className="font-mono-tabular font-bold">{t.symbol}</td>
                              <td className="!text-right font-mono-tabular">{fmtNum(t.quantity, 2)}</td>
                              <td className="!text-right font-mono-tabular">{fmtMoney(t.price)}</td>
                              <td className="!text-right font-mono-tabular text-slate-400">{fmtMoney(t.commission)}</td>
                              <td className={`!text-right font-mono-tabular ${t.trade_type === "SELL" ? (t.realized_pnl >= 0 ? "ticker-up" : "ticker-down") : "text-slate-600"}`}>
                                {t.trade_type === "SELL" ? fmtMoney(t.realized_pnl) : "—"}
                              </td>
                              <td className="text-xs text-slate-300">
                                {t.created_by_username || "—"}
                                {t.updated_by_username && <div className="text-amber-400 text-[10px]">edited by {t.updated_by_username}</div>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="label-caps !text-slate-400">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const Stat = ({ label, value, tone = "default" }) => {
  const color = tone === "up" ? "text-emerald-400" : tone === "down" ? "text-rose-400" : "text-white";
  return (
    <div className="p-3 rounded-lg border border-slate-800 bg-slate-900/40">
      <div className="label-caps">{label}</div>
      <div className={`metric-value text-lg mt-1 ${color}`}>{value}</div>
    </div>
  );
};

const Chip = ({ label, value, tone = "default" }) => {
  const color = tone === "up" ? "text-emerald-400" : tone === "down" ? "text-rose-400" : "text-slate-200";
  return (
    <div className="px-2.5 py-1 rounded-md border border-slate-800 bg-slate-900/40">
      <span className="label-caps mr-2">{label}</span>
      <span className={`font-mono-tabular text-xs ${color}`}>{value}</span>
    </div>
  );
};
